import { Card, Progress } from "@dawa/ui";
import { AlertCircle, CheckCircle2, ListChecks, Target } from "lucide-react";

type SeoAssistantCardProps = {
  article: string;
  meta: any;
  primaryKeyword: string;
  secondaryKeywords: string[];
  serp: any | null;
};

type ChecklistStatus = "ok" | "warn" | "info";

const DENSITY_RANGE = { min: 0.5, max: 2.5 };

function normalizeText(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function countOccurrences(text: string, term: string) {
  const cleaned = term.trim();
  if (!cleaned) return 0;
  const escaped = cleaned.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`\\b${escaped}\\b`, "gi");
  return (text.match(re) || []).length;
}

function statusColor(status: ChecklistStatus) {
  if (status === "ok") return "bg-success/10 text-success";
  if (status === "warn") return "bg-warning/10 text-warning";
  return "bg-surface text-muted";
}

function statusIcon(status: ChecklistStatus) {
  if (status === "ok") return <CheckCircle2 className="h-4 w-4 text-success" />;
  if (status === "warn") return <AlertCircle className="h-4 w-4 text-warning" />;
  return <Target className="h-4 w-4 text-muted" />;
}

export function SeoAssistantCard({
  article,
  meta,
  primaryKeyword,
  secondaryKeywords,
  serp,
}: SeoAssistantCardProps) {
  const text = article || "";
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const h1 = (text.match(/^#\s+/gm) || []).length;
  const h2 = (text.match(/^##\s+/gm) || []).length;
  const h3 = (text.match(/^###\s+/gm) || []).length;
  const firstH1 = /^#\s+(.+)$/m.exec(text)?.[1] || "";
  const intro = text.split(/\n/).find((line) => line.trim().length > 0) || "";
  const normalizedText = normalizeText(text);
  const normalizedPrimary = normalizeText(primaryKeyword || "");
  const primaryCount = normalizedPrimary ? countOccurrences(normalizedText, normalizedPrimary) : 0;
  const density = wordCount > 0 ? (primaryCount / wordCount) * 100 : 0;

  const secondaryList = Array.isArray(secondaryKeywords) ? secondaryKeywords : [];
  const normalizedSecondary = secondaryList.map((k) => ({
    raw: k,
    normalized: normalizeText(k),
  }));

  const serpSubtopics = Array.isArray(serp?.subtopics) ? serp.subtopics : [];
  const serpQuestions = Array.isArray(serp?.questions) ? serp.questions : [];

  const checklist: Array<{ label: string; status: ChecklistStatus; detail?: string }> = [
    {
      label: "H1 unique",
      status: h1 === 1 ? "ok" : "warn",
      detail: h1 === 1 ? "OK" : `H1 trouvés: ${h1}`,
    },
    {
      label: "Mot-clé dans le H1",
      status: normalizedPrimary
        ? normalizeText(firstH1).includes(normalizedPrimary)
          ? "ok"
          : "warn"
        : "info",
      detail: normalizedPrimary ? firstH1 || "H1 manquant" : "Définir le mot-clé principal",
    },
    {
      label: "Mot-clé dans l'intro",
      status: normalizedPrimary
        ? normalizeText(intro).includes(normalizedPrimary)
          ? "ok"
          : "warn"
        : "info",
      detail: intro ? intro.slice(0, 60) + (intro.length > 60 ? "…" : "") : "Intro manquante",
    },
    {
      label: "Structure H2/H3",
      status: h2 >= 3 ? "ok" : "warn",
      detail: `H2/H3: ${h2}/${h3}`,
    },
    {
      label: "Longueur",
      status: wordCount >= 600 ? "ok" : "warn",
      detail: `${wordCount} mots`,
    },
    {
      label: "Meta title/description",
      status: meta?.title && meta?.description ? "ok" : "warn",
      detail: meta?.title && meta?.description ? "OK" : "Meta incomplètes",
    },
  ];

  const missingSecondary = normalizedSecondary
    .filter(({ normalized }) => normalized && !normalizedText.includes(normalized))
    .map(({ raw }) => raw);

  const missingSubtopics = serpSubtopics.filter(
    (topic: string) => topic && !normalizedText.includes(normalizeText(topic)),
  );
  const missingQuestions = serpQuestions.filter(
    (question: string) => question && !normalizedText.includes(normalizeText(question)),
  );

  const actions: string[] = [];
  if (normalizedPrimary && primaryCount === 0)
    actions.push("Ajouter le mot-clé principal dans le texte.");
  if (density > 0 && (density < DENSITY_RANGE.min || density > DENSITY_RANGE.max)) {
    actions.push(`Ajuster la densité du mot-clé (${density.toFixed(2)}%).`);
  }
  if (missingSecondary.length > 0) {
    actions.push(`Intégrer ${missingSecondary.slice(0, 3).join(", ")}.`);
  }
  if (h2 < 3) actions.push("Ajouter plus de sections H2 pour couvrir l'intention.");
  if (missingSubtopics.length > 0) {
    actions.push(`Couvrir les sous-thèmes: ${missingSubtopics.slice(0, 3).join(", ")}.`);
  }
  if (missingQuestions.length > 0) {
    actions.push("Ajouter une FAQ basée sur la SERP.");
  }

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-center gap-2 text-base font-semibold">
        <ListChecks className="h-4 w-4 text-muted" />
        Assistant SEO
      </div>

      <div className="rounded-lg border border-muted/20 bg-surface/40 p-3">
        <div className="text-sm font-medium text-text">Mot-clé principal</div>
        <div className="mt-1 text-xs text-muted">{primaryKeyword || "Non défini"}</div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted">
          <span>{primaryCount} occurrence(s)</span>
          <span>Densité: {density.toFixed(2)}%</span>
          <span>
            Repère: {DENSITY_RANGE.min}–{DENSITY_RANGE.max}%
          </span>
        </div>
        <Progress
          value={Math.min(100, (density / 3) * 100)}
          size="sm"
          variant={
            density >= DENSITY_RANGE.min && density <= DENSITY_RANGE.max ? "success" : "warning"
          }
          className="mt-2"
        />
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-text">Checklist rédactionnelle</div>
        <div className="grid gap-2 md:grid-cols-2">
          {checklist.map((item) => (
            <div
              key={item.label}
              className="flex items-start gap-2 rounded-md border border-muted/20 bg-surface/40 p-2"
            >
              {statusIcon(item.status)}
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {item.label}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] ${statusColor(item.status)}`}
                  >
                    {item.status === "ok" ? "OK" : item.status === "warn" ? "À revoir" : "Info"}
                  </span>
                </div>
                {item.detail && <div className="text-xs text-muted">{item.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-text">Mots-clés secondaires</div>
        {secondaryList.length === 0 ? (
          <div className="text-xs text-muted">Aucun mot-clé secondaire défini.</div>
        ) : (
          <div className="flex flex-wrap gap-2 text-xs">
            {normalizedSecondary.map(({ raw, normalized }) => {
              const present = normalized && normalizedText.includes(normalized);
              return (
                <span
                  key={raw}
                  className={`rounded-full px-2 py-1 ${present ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}
                >
                  {raw}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-text">Sous-thèmes SERP</div>
        {serpSubtopics.length === 0 ? (
          <div className="text-xs text-muted">
            Aucun sous-thème disponible (lancer l'analyse SERP).
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 text-xs">
            {serpSubtopics.slice(0, 12).map((topic: string) => {
              const present = normalizedText.includes(normalizeText(topic));
              return (
                <span
                  key={topic}
                  className={`rounded-full px-2 py-1 ${present ? "bg-success/10 text-success" : "bg-muted/30 text-muted"}`}
                >
                  {topic}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-text">Questions PAA</div>
        {serpQuestions.length === 0 ? (
          <div className="text-xs text-muted">Aucune question PAA disponible.</div>
        ) : (
          <ul className="space-y-1 text-xs text-muted">
            {serpQuestions.slice(0, 6).map((question: string) => {
              const present = normalizedText.includes(normalizeText(question));
              return (
                <li key={question} className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${present ? "bg-success" : "bg-muted"}`} />
                  <span className={present ? "text-text" : ""}>{question}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-text">Actions rapides</div>
        {actions.length === 0 ? (
          <div className="text-xs text-muted">Rien de bloquant détecté.</div>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted">
            {actions.slice(0, 5).map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
